import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  WhatsappLogo, ArrowsClockwise, ChatCircle, Lightning,
  PaperPlaneRight, ArrowCounterClockwise, CircleNotch,
} from "@phosphor-icons/react";
import WhatsAppBubble from "@/components/operacao/WhatsAppBubble";
import { useSector } from "@/lib/sectorContext";

/**
 * LivePreview — two modes share the same right-rail container:
 *
 *   "Perguntas rápidas" (legacy, cheap)
 *      Cycles through 4 curated questions and renders a LOCAL,
 *      grounded answer by hitting /api/operacao/tools/*. No LLM cost,
 *      no orchestrator. Great for sanity-checking that prices / hours
 *      / promos / availability are wired up.
 *
 *   "Conversar livre" (NEW — full parity)
 *      Free-form chat box. Each turn hits POST /api/operacao/playground/turn
 *      which routes through the EXACT orchestrator used in production
 *      (Claude Sonnet 4.5, OIL grounding, identity/policies). The
 *      `session_id` keeps multi-turn memory continuous so the operator
 *      can stress-test long conversations the way a real customer would
 *      experience them. Zero side effects: no Z-API dispatch, no
 *      audit log, no DB write.
 *
 * Why ONE component for both modes? Operators need fast triage
 * ("preços tão certos?") AND deep validation ("a IA mantém contexto
 * em 5 turns?"). A toggle on top keeps the mental model: same surface,
 * different intensity.
 */

const DEMO_QUESTIONS = [
  { q: "Quanto fica troca de óleo?", kind: "price", arg: "troca de óleo" },
  { q: "Quando vocês abrem amanhã?", kind: "hours", arg: null },
  { q: "Tem promoção hoje?", kind: "promotion", arg: null },
  { q: "Tem horário pra amanhã?", kind: "availability", arg: null },
];

// v0.98 · Fev/2026 — Perguntas rápidas por setor. Cada setor tem sua
// pergunta de "preço" ancorada no serviço típico da vertical. Chips
// de horário/promoção/disponibilidade são iguais em todos.
const DEMO_QUESTIONS_BY_SECTOR = {
  automotive: [
    { q: "Quanto fica troca de óleo?", kind: "price", arg: "troca de óleo" },
  ],
  petshop: [
    { q: "Quanto fica banho e tosa?", kind: "price", arg: "banho e tosa" },
  ],
  clinic: [
    { q: "Quanto fica a consulta?", kind: "price", arg: "consulta" },
  ],
  salon: [
    { q: "Quanto fica escova?", kind: "price", arg: "escova" },
  ],
  gym: [
    { q: "Quanto fica a mensalidade?", kind: "price", arg: "mensalidade" },
  ],
  real_estate: [
    { q: "Vocês têm apartamento de 2 quartos?", kind: "price", arg: "2 quartos" },
  ],
  hotel: [
    { q: "Quanto fica a diária?", kind: "price", arg: "diária" },
  ],
  restaurant: [
    { q: "Quanto fica o prato executivo?", kind: "price", arg: "executivo" },
  ],
  tech_repair: [
    { q: "Quanto fica trocar tela?", kind: "price", arg: "tela" },
  ],
  real_estate_default: [
    { q: "Vocês têm imóvel disponível?", kind: "price", arg: "imóvel" },
  ],
  // v0.99.50 · Fev/2026 — chips neutros para setores sem catálogo de
  // "serviços pagos" (Igreja, Mandato, Prefeitura…) e para os
  // verticais que faltavam na v0.98.
  church: [
    { q: "Como participo das células?", kind: "price", arg: "célula" },
  ],
  mandate: [
    { q: "Como faço pra protocolar uma demanda?", kind: "price", arg: "demanda" },
  ],
  gov_municipal: [
    { q: "Como abro um protocolo na prefeitura?", kind: "price", arg: "protocolo" },
  ],
  gov_secretariat: [
    { q: "Como faço um protocolo nessa secretaria?", kind: "price", arg: "protocolo" },
  ],
  gov_chamber: [
    { q: "Como acompanho uma sessão da câmara?", kind: "price", arg: "sessão" },
  ],
  gov_state: [
    { q: "Como protocolo um pedido no órgão?", kind: "price", arg: "protocolo" },
  ],
  pharmacy: [
    { q: "Vocês entregam remédio em casa?", kind: "price", arg: "entrega" },
  ],
  accounting_firm: [
    { q: "Quanto custa a mensalidade contábil?", kind: "price", arg: "honorário" },
  ],
  law_firm: [
    { q: "Como funciona a consulta jurídica?", kind: "price", arg: "consulta" },
  ],
  insurance_broker: [
    { q: "Quanto fica o seguro do meu carro?", kind: "price", arg: "seguro" },
  ],
  school: [
    { q: "Qual o valor da mensalidade?", kind: "price", arg: "mensalidade" },
  ],
  events: [
    { q: "Vocês fazem orçamento pra 100 pessoas?", kind: "price", arg: "pacote" },
  ],
  supermarket: [
    { q: "Vocês fazem entrega hoje?", kind: "price", arg: "entrega" },
  ],
  construction_supply: [
    { q: "Vocês têm cimento em oferta?", kind: "price", arg: "cimento" },
  ],
  furniture_store: [
    { q: "Vocês parcelam o sofá em quantas vezes?", kind: "price", arg: "sofá" },
  ],
  distributor: [
    { q: "Qual o pedido mínimo pra atacado?", kind: "price", arg: "atacado" },
  ],
  industry: [
    { q: "Vocês vendem pra consumidor final?", kind: "price", arg: "orçamento" },
  ],
  logistics: [
    { q: "Qual o prazo pro transporte dessa carga?", kind: "price", arg: "frete" },
  ],
  condominium: [
    { q: "Como solicito a 2ª via do boleto?", kind: "price", arg: "boleto" },
  ],
  service_provider: [
    { q: "Vocês fazem orçamento no local?", kind: "price", arg: "orçamento" },
  ],
};

const DEMO_QUESTIONS_COMMON = [
  { q: "Quando vocês abrem amanhã?", kind: "hours", arg: null },
  { q: "Tem promoção hoje?", kind: "promotion", arg: null },
  { q: "Tem horário pra amanhã?", kind: "availability", arg: null },
];

function questionsForSector(code) {
  const specific =
    DEMO_QUESTIONS_BY_SECTOR[code] ||
    DEMO_QUESTIONS_BY_SECTOR.automotive;
  return [...specific, ...DEMO_QUESTIONS_COMMON];
}

function fmtBRL(cents) {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", minimumFractionDigits: 0,
  });
}

function composeReply(kind, data, policy) {
  // Iter-71: when the workshop set "Nunca informa preço" we must
  // NEVER quote a value in the preview — same contract as the
  // production orchestrator (REGRA ABSOLUTA in agent_orchestrator).
  const hidesPrice = policy
    && policy.toLowerCase().startsWith("nunca informa pre");
  if (kind === "price") {
    if (hidesPrice) {
      return "Já te confirmo certinho! Só 1 minutinho 😉";
    }
    const m = data?.match;
    if (!m) return "Posso confirmar essa informação com a equipe e te retorno em instantes 😊";
    return `${m.name} fica ${fmtBRL(m.price_cents)} — leva uns ${m.duration_min ?? 60} min. Quer agendar?`;
  }
  if (kind === "promotion") {
    const list = data?.promotions || [];
    if (list.length === 0) return "No momento estamos sem campanhas ativas, mas posso te apresentar nossas condições padrão. Em qual serviço você está pensando?";
    if (hidesPrice) {
      // Same policy — never disclose the promo value in preview.
      const top = list[0];
      return `Sim, temos *${top.title}* rolando! Já te confirmo os detalhes — 1 minutinho 😉`;
    }
    const top = list[0];
    return `Sim! Estamos com *${top.title}*${top.price_cents ? ` — ${fmtBRL(top.price_cents)}` : ""}. Quer que eu reserve?`;
  }
  if (kind === "hours") {
    if (!data?.configured) return "Posso confirmar nosso horário com a equipe e te retorno em instantes 😊";
    const weekly = data.weekly || {};
    const tomorrow = weekly.tuesday || weekly.monday || "—";
    return `Amanhã estamos atendendo: ${tomorrow}. Quer aproveitar e já agendar algo?`;
  }
  if (kind === "availability") {
    const slots = data?.slots || [];
    if (slots.length === 0) return "Posso confirmar a melhor janela com a equipe e te retorno em instantes 😊";
    const opts = [slots[0], slots[1]].filter(Boolean).map((s) => `${s.date} às ${s.time}`).join(" ou ");
    return `Tenho disponibilidade em ${opts}. Qual prefere?`;
  }
  return "—";
}

function freshSessionId() {
  return `pg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

// ── Quick-questions mode ─────────────────────────────────────────────────

function QuickMode() {
  // v0.98 · Fev/2026 — Perguntas rápidas dinâmicas por setor do tenant.
  const { profile } = useSector();
  const questions = useMemo(
    () => questionsForSector(profile?.code || "automotive"),
    [profile?.code],
  );
  const [selected, setSelected] = useState(questions[0]);
  // Se o setor mudar (ex: operador troca no admin), reseta pergunta.
  useEffect(() => { setSelected(questions[0]); }, [questions]);
  const [reply, setReply] = useState(null);
  const [loading, setLoading] = useState(false);
  // Iter-71: cache the negotiation policy so QuickMode honours
  // "Nunca informa preço" the same way the production orchestrator does.
  const [policy, setPolicy] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetchPolicy = async () => {
      try {
        // Iter-71.1: `negotiation_policy` lives on OperationalIdentity
        // (route `/operacao/identity`), NOT on `/reception/settings`.
        // The earlier endpoint returned `undefined` so QuickMode always
        // fell through to the "full" branch and leaked prices in the
        // preview even when the operator picked "Nunca informa preço".
        const res = await api.get("/operacao/identity");
        if (!cancelled) setPolicy(res.data?.negotiation_policy || "");
      } catch (_e) {
        if (!cancelled) setPolicy("");
      }
    };
    fetchPolicy();
    // Reactively refresh when the operator saves the Identity tab so
    // the preview never lags behind the configured policy.
    const onIdentityUpdate = () => fetchPolicy();
    window.addEventListener("agentus:identity-updated", onIdentityUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("agentus:identity-updated", onIdentityUpdate);
    };
  }, []);

  const refresh = async (q = selected) => {
    setLoading(true);
    try {
      let res;
      if (q.kind === "price") res = await api.get(`/operacao/tools/service_price?query=${encodeURIComponent(q.arg)}`);
      else if (q.kind === "hours") res = await api.get("/operacao/tools/business_hours");
      else if (q.kind === "promotion") res = await api.get("/operacao/tools/active_promotions");
      else if (q.kind === "availability") res = await api.get("/operacao/tools/availability?limit=4");
      setReply(composeReply(q.kind, res.data, policy));
    } catch (e) {
      setReply("⚠️ Não consegui consultar a base agora. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(selected); /* eslint-disable-next-line */ }, [selected, policy]);

  return (
    <>
      <div className="px-4 py-3 space-y-2 border-b border-slate-100 bg-white/60">
        {questions.map((q) => (
          <button
            key={q.q}
            type="button"
            data-testid={`live-preview-q-${q.kind}`}
            onClick={() => setSelected(q)}
            className={`w-full text-left text-[12px] px-3 py-1.5 rounded-full border transition-colors ${
              selected.q === q.q
                ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {q.q}
          </button>
        ))}
      </div>

      <div
        className="px-4 py-5 space-y-2"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.04) 1px, transparent 0)",
          backgroundSize: "12px 12px",
          backgroundColor: "#efeae2",
        }}
      >
        <div className="flex justify-end">
          <div className="max-w-[85%] px-3 py-1.5 rounded-2xl rounded-br-md bg-[#d9fdd3] text-[13px] text-slate-900 shadow-sm">
            {selected.q}
          </div>
        </div>
        <div className="flex justify-start">
          <div
            data-testid="operacao-live-preview-reply"
            className="max-w-[88%] px-3 py-2 rounded-2xl rounded-bl-md bg-white border border-slate-100 text-[13px] text-slate-900 whitespace-pre-wrap shadow-sm"
          >
            {loading ? <CircleNotch size={14} className="animate-spin text-slate-400" /> : (reply || "—")}
          </div>
        </div>
        <div className="text-[10px] text-slate-500 text-center pt-2 bg-white/60 -mx-4 -mb-5 px-4 py-2">
          baseado nos dados atualmente cadastrados
        </div>
      </div>
    </>
  );
}

// ── Free-chat mode (true production parity) ──────────────────────────────

function ChatMode() {
  const { profile } = useSector();
  const [sessionId, setSessionId] = useState(freshSessionId);
  const [messages, setMessages] = useState([]);  // [{role, text}]
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("lead"); // lead | customer
  const scrollRef = useRef(null);

  useEffect(() => {
    // auto-scroll to bottom on new message
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || sending) return;
    setInput("");
    const userMsg = { role: "user", text };
    // Snapshot the transcript BEFORE optimistically appending the new
    // user message — this is what the backend uses for "memory" so it
    // matches the AI's understanding of the conversation up to this turn.
    const historyForRequest = messages.map((m) => ({
      role: m.role === "user" ? "user" : "ai",
      text: m.text || "",
    }));
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);
    try {
      // Nome do cliente segue a regra real da operação:
      //   • lead novo  → NUNCA enviamos um nome (a IA deve abordar
      //     anonimamente e perguntar pelo nome, exatamente como faria
      //     ao receber um número desconhecido no WhatsApp).
      //   • cliente conhecido → enviamos um nome plausível pra simular
      //     um perfil já cadastrado no CRM.
      // O bug anterior enviava "João Silva" hardcoded em todos os casos,
      // o que fazia a IA cumprimentar pelo nome mesmo no fluxo de lead
      // ("Oi, João!") — comportamento inaceitável: passa a impressão de
      // que a ferramenta deduz nomes.
      const customer_name = status === "customer" ? "João Silva" : null;
      const res = await api.post("/operacao/playground/turn", {
        session_id: sessionId,
        customer_name,
        customer_message: text,
        customer_status: status,
        history: historyForRequest,
      });
      setMessages((prev) => [
        ...prev,
        { role: "ai", text: res.data.reply || "—", intent: res.data.intent, action: res.data.propose_action },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: "ai", text: "⚠️ Não consegui responder agora. Tente de novo.", error: true },
      ]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(freshSessionId());
  };

  // v0.99.50 — starters do chat livre dependem do setor. Pergunta "preço"
  // vem do mapa compartilhado com QuickMode; o resto é neutro.
  const QUICK_STARTERS = useMemo(() => {
    const code = profile?.code || "automotive";
    const sectorPrice = (DEMO_QUESTIONS_BY_SECTOR[code] || DEMO_QUESTIONS_BY_SECTOR.automotive)[0];
    return [
      `Oi, queria saber mais sobre ${sectorPrice?.arg || "preço"}`,
      "Tem horário pra amanhã de manhã?",
      "Tem alguma promoção ativa hoje?",
    ];
  }, [profile?.code]);

  return (
    <div className="flex flex-col" style={{ height: "min(560px, 70vh)" }}>
      <div className="px-4 py-2 border-b border-slate-100 bg-white/60 flex items-center justify-between gap-2 text-[11px]">
        <div className="flex items-center gap-1">
          <span className="text-slate-500">Cliente é</span>
          <button
            type="button"
            data-testid="chat-status-lead"
            onClick={() => setStatus("lead")}
            className={`px-2 py-0.5 rounded-full ${status === "lead" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            lead novo
          </button>
          <button
            type="button"
            data-testid="chat-status-customer"
            onClick={() => setStatus("customer")}
            className={`px-2 py-0.5 rounded-full ${status === "customer" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            cliente conhecido
          </button>
        </div>
        <button
          type="button"
          data-testid="chat-reset"
          onClick={reset}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100"
          title="Reiniciar conversa"
        >
          <ArrowCounterClockwise size={11} weight="bold" />
          Reiniciar
        </button>
      </div>

      <div
        ref={scrollRef}
        data-testid="chat-stream"
        className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.04) 1px, transparent 0)",
          backgroundSize: "12px 12px",
          backgroundColor: "#efeae2",
        }}
      >
        {messages.length === 0 && (
          <div className="flex flex-col gap-2 mt-1">
            <p className="text-[11px] text-slate-500 px-1">
              Digite como se fosse seu cliente. A IA responde com o mesmo motor que roda em produção.
            </p>
            <div className="flex flex-col gap-1.5 mt-1">
              {QUICK_STARTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`chat-starter-${s.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`}
                  onClick={() => send(s)}
                  className="text-left text-[12px] px-3 py-1.5 rounded-full border border-slate-200 bg-white/80 text-slate-700 hover:border-slate-900 hover:text-slate-900 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className="flex flex-col gap-0.5">
            <WhatsAppBubble
              text={m.text}
              variant={m.role === "user" ? "outbound" : "inbound"}
              testId={`chat-bubble-${m.role}-${i}`}
            />
            {m.role === "ai" && m.action && m.action.code && m.action.code !== "none" && (
              <span className="self-start text-[10px] text-slate-500 ml-1 italic" data-testid={`chat-action-${i}`}>
                ↳ ação proposta: {m.action.code}
              </span>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="chat-typing">
            <div className="px-3 py-2 rounded-2xl rounded-bl-md bg-white border border-slate-100 text-[13px] text-slate-400 shadow-sm">
              <span className="inline-flex items-center gap-1.5">
                <CircleNotch size={11} className="animate-spin" />
                IA está pensando…
              </span>
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="px-3 py-3 border-t border-slate-100 bg-white flex items-center gap-2"
      >
        <input
          type="text"
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Digite como cliente…"
          disabled={sending}
          className="flex-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none disabled:bg-slate-50"
        />
        <button
          type="submit"
          data-testid="chat-send"
          disabled={sending || !input.trim()}
          className="shrink-0 h-9 w-9 rounded-full bg-slate-900 hover:bg-slate-800 text-white inline-flex items-center justify-center disabled:opacity-30 transition-colors"
          aria-label="Enviar"
        >
          <PaperPlaneRight size={14} weight="fill" />
        </button>
      </form>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────

export default function LivePreview() {
  const [mode, setMode] = useState("quick"); // "quick" | "chat"

  return (
    <aside
      data-testid="operacao-live-preview"
      className="sticky top-6 w-[360px] shrink-0 rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm"
    >
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2 mb-3">
          <WhatsappLogo size={18} weight="fill" className="text-emerald-600" />
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">
              Preview ao vivo
            </div>
            <div className="text-sm font-semibold text-slate-900 leading-tight">
              {mode === "chat" ? "Converse com o Agentus" : "Como o Agentus responderia"}
            </div>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-1 p-0.5 rounded-full bg-slate-100 text-[12px]">
          <button
            type="button"
            data-testid="live-preview-mode-quick"
            onClick={() => setMode("quick")}
            className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full transition-all ${
              mode === "quick" ? "bg-white shadow-sm text-slate-900 font-medium" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            <Lightning size={11} weight={mode === "quick" ? "fill" : "regular"} />
            Perguntas rápidas
          </button>
          <button
            type="button"
            data-testid="live-preview-mode-chat"
            onClick={() => setMode("chat")}
            className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full transition-all ${
              mode === "chat" ? "bg-white shadow-sm text-slate-900 font-medium" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            <ChatCircle size={11} weight={mode === "chat" ? "fill" : "regular"} />
            Conversar livre
          </button>
        </div>
      </div>

      {mode === "quick" ? <QuickMode /> : <ChatMode />}
    </aside>
  );
}
